import { clearStoredAuth, getStoredAuth } from "./token-storage";

const API_URL = import.meta.env.VITE_API_URL ?? "https://api.tenbagger.xyz";

function isPublicPath(path: string, method: string): boolean {
	if (
		path === "/v1/ping" ||
		path === "/v1/price" ||
		path === "/v1/auth/email/send-code" ||
		path === "/v1/auth/email/verify"
	) {
		return true;
	}
	// GET /v1/referrals/:code is public (pre-signup code validation)
	if (method === "GET" && /^\/v1\/referrals\/[^/?]+(\?|$)/.test(path)) {
		return true;
	}
	return false;
}

export interface ApiErrorPayload {
	status: number;
	error: string;
}

export class ApiError extends Error {
	readonly status: number;
	readonly payload: unknown;

	constructor(status: number, message: string, payload: unknown) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.payload = payload;
	}
}

/**
 * Expected 401: the stored session token aged out (clock skew or a backend
 * deploy / key rotation can also invalidate it server-side while the client
 * still considers it valid). `apiFetch` clears the stale token on a 401, which
 * flips the app to logged-out; the user re-authenticates via the email-code
 * dialog (no refresh tokens — sessions can't be renewed silently). The
 * TanStack Query/Mutation `onError` handlers use this to drop these 401s from
 * Sentry as expected noise.
 */
export function isUnauthorizedError(error: unknown): boolean {
	return error instanceof ApiError && error.status === 401;
}

interface ApiFetchOptions extends Omit<RequestInit, "body"> {
	body?: unknown;
}

export async function apiFetch<T>(
	path: string,
	options: ApiFetchOptions = {},
): Promise<T> {
	const { body, headers, ...rest } = options;

	const finalHeaders = new Headers(headers ?? {});
	if (body !== undefined && !finalHeaders.has("Content-Type")) {
		finalHeaders.set("Content-Type", "application/json");
	}

	const method = (rest.method ?? "GET").toUpperCase();
	const publicPath = isPublicPath(path, method);

	if (!publicPath) {
		const auth = getStoredAuth();
		if (auth?.access_token) {
			finalHeaders.set("Authorization", `Bearer ${auth.access_token}`);
		}
	}

	const res = await fetch(`${API_URL}${path}`, {
		...rest,
		headers: finalHeaders,
		body: body === undefined ? undefined : JSON.stringify(body),
	});

	const text = await res.text();
	const json = text ? safeParse(text) : null;

	if (!res.ok) {
		if (res.status === 401 && !publicPath) {
			clearStoredAuth();
		}
		const message =
			(json && typeof json === "object" && "error" in json
				? String((json as ApiErrorPayload).error)
				: null) ?? `request failed (${res.status})`;
		throw new ApiError(res.status, message, json);
	}

	if (json && typeof json === "object" && "data" in json) {
		return (json as { data: T }).data;
	}
	return json as T;
}

function safeParse(text: string): unknown {
	try {
		return JSON.parse(text);
	} catch {
		return null;
	}
}
